// TableViewCompact.jsx

import React, { useMemo } from 'react';
import { isEnumCompatibleFieldType } from 'utilities/FieldTypes';

// Utilities
import { getEnumOptionLabel } from 'utilities/EnumOptions';
import {
	buildTableColumns,
	extractViewColumnWidths,
} from 'views/table/utils/tableColumnUtils';
import {
	formatFieldValue,
	getFieldType,
	resolveFieldValue,
} from 'views/table/utils/tableFieldUtils';

// Hooks
import useTableNodelistHydration from 'views/table/hooks/useTableNodelistHydration';
import useTableSchema from 'views/table/hooks/useTableSchema';

// CSS
import './TableViewCompact.css';

const normalizeString = (value) => String(value || '').trim();

const getPeopleSummary = (value) => (
	(Array.isArray(value) ? value : [])
		.map((entry) => normalizeString(
			entry?.data_profile?.name
			|| entry?.name
			|| entry?.title
			|| entry?.email
			|| entry?.userKey
			|| entry?.key
		))
		.filter(Boolean)
		.join(', ')
);

const getChecklistSummary = (value) => {
	const totalItems = Number(value?.totalItems) || 0;
	const completedItems = Number(value?.completedItems) || 0;
	if (totalItems > 0) {
		return `${completedItems}/${totalItems}`;
	}

	const items = (Array.isArray(value) ? value : [])
		.filter((entry) => entry && typeof entry === 'object' && normalizeString(entry?.text));

	if (!items.length) return '';

	const completedCount = items.filter((entry) => (
		Boolean(entry?.checked) || String(entry?.status || '').toLowerCase() === 'done'
	)).length;
	return `${completedCount}/${items.length}`;
};

const getCompactCellValue = ({
	fieldDataSection,
	fieldDef,
	fieldKey,
	language,
	node,
	resolveNodelistTitle,
}) => {
	if (fieldKey === 'title') {
		return normalizeString(node?.title) || 'Untitled';
	}

	const resolvedValue = resolveFieldValue(node, fieldDef, fieldDataSection);
	const fieldType = getFieldType(fieldDef, fieldKey);
	const formatOptions = { resolveNodelistTitle };

	if (isEnumCompatibleFieldType(fieldType)) {
		return getEnumOptionLabel(fieldDef?.options || [], resolvedValue, language)
			|| formatFieldValue(resolvedValue, fieldDef, fieldKey, formatOptions);
	}

	if (fieldType === 'multiselect') {
		const values = Array.isArray(resolvedValue) ? resolvedValue : [resolvedValue];
		return values
			.map((value) => getEnumOptionLabel(fieldDef?.options || [], value, language) || normalizeString(value))
			.filter(Boolean)
			.join(', ');
	}

	if (fieldType === 'people') {
		return getPeopleSummary(resolvedValue);
	}

	if (fieldType === 'checklist') {
		return getChecklistSummary(resolvedValue);
	}

	return formatFieldValue(resolvedValue, fieldDef, fieldKey, formatOptions);
};

/**
 * TableViewCompact
 *
 * Renders a read-only compact table for constrained surfaces such as page blocks.
 *
 * @param {boolean} [enforceExplicitProperties=false] - Whether empty properties should render title only.
 * @param {string} [language='en'] - Active language code.
 * @param {Array<object>} [nodes=[]] - Rows to render.
 * @param {Function|null} [onRowClick=null] - Optional row click handler for future detail affordances.
 * @param {object|null} typeDef - Type definition for the rendered rows.
 * @param {object|null} view - View definition driving properties, filters, and sorts.
 *
 * @returns {ReactNode} The rendered compact table.
 */
const TableViewCompact = React.memo(({
	enforceExplicitProperties = false,
	language = 'en',
	nodes = [],
	onRowClick = null,
	typeDef,
	view,
}) => {

	// ----- HOOKS ----- //

	const {
		fieldDataSection,
		schemaFieldMap,
		visibleFieldKeys,
	} = useTableSchema({
		enforceExplicitProperties,
		typeDef,
		view,
	});

	// ----- MEMOS ----- //

	const columns = useMemo(() => {
		const effectiveColumnWidths = extractViewColumnWidths(view?.columnWidths);
		return buildTableColumns({
			effectiveColumnWidths,
			schemaFieldMap,
			visibleFieldKeys,
		}).filter((column) => column.key !== 'open');
	}, [schemaFieldMap, view?.columnWidths, visibleFieldKeys]);

	const rowItems = Array.isArray(nodes) ? nodes : [];
	const columnFieldKeys = useMemo(
		() => columns.map((column) => column.key),
		[columns],
	);
	const { resolveNodelistTitle } = useTableNodelistHydration({
		nodes: rowItems,
		schemaFieldMap,
		fieldKeys: columnFieldKeys,
		fieldDataSection,
	});
	const rowIsClickable = typeof onRowClick === 'function';
	// ----- RENDER ----- //

	return (
		<div className="table-view-compact-container">

			{/* SCROLLER */}
			<div className="table-view-compact-scroller">

				{/* TABLE */}
				<table className="table-view-compact-table">
					<thead>
						<tr className="table-view-compact-header-row">
							{columns.map((column) => (
								<th
									className="table-view-compact-header-cell"
									style={{
										width: column.width || 200,
									}}
									key={column.key}
									scope="col"
								>
									{column.label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rowItems.map((node, rowIndex) => (
							<tr
								className={[
									'table-view-compact-row',
									rowIsClickable ? 'table-view-compact-row-clickable' : '',
								].filter(Boolean).join(' ')}
								key={node?.key || `row-${rowIndex}`}
								onClick={rowIsClickable ? (event) => {
									event?.stopPropagation?.();
									onRowClick(node);
								} : undefined}
							>
								{columns.map((column) => {
									const fieldDef = schemaFieldMap.get(column.key) || null;
									const value = getCompactCellValue({
										fieldDataSection,
										fieldDef,
										fieldKey: column.key,
										language,
										node,
										resolveNodelistTitle,
									});

									return (
										<td
											className="table-view-compact-cell"
											style={{
												width: column.width || 200,
											}}
											key={`${node?.key || 'row'}:${column.key}`}
										>

											{/* CELL VALUE */}
											<div
												className="table-view-compact-cell-value"
												title={normalizeString(value)}
											>
												{value}
											</div>
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
});

// Dev Tools
TableViewCompact.displayName = 'TableViewCompact';

export default TableViewCompact;
